import { motion } from "framer-motion";
import { Mic, Youtube, Facebook, Music2, Instagram, ArrowUpRight } from "lucide-react";

const ENGINES = [
  {
    icon: Mic,
    name: "Podcast Engine",
    phase: "Phase A",
    copy: "Discover podcasts whose listeners match your buyer. AI drafts the interview angle and the value you bring before any pitch.",
    detail: "Pipeline: Discovered → Researched → Booked → Appeared → Leads → Sales",
    span: "lg:col-span-2 lg:row-span-2",
    image: "https://images.unsplash.com/photo-1485579149621-3123dd979885?auto=format&fit=crop&w=1200&q=80",
  },
  {
    icon: Youtube,
    name: "YouTube Engine",
    phase: "Phase A",
    copy: "Find creators, read their comment sections for intent, and engage with thoughtful, approved contributions — never automated spam.",
    detail: "AI Engagement Assistant with human approve / edit / reject",
    span: "lg:col-span-2",
  },
  {
    icon: Facebook,
    name: "Facebook Engine",
    phase: "Phase B",
    copy: "Map active groups, respect their rules, and build a Community Value Plan that makes the admin look like a hero.",
    detail: "Admin-first partnership approach",
    span: "lg:col-span-1",
  },
  {
    icon: Music2,
    name: "TikTok Engine",
    phase: "Phase C",
    copy: "Detect high-intent conversations and route the right resource to the right person at the right moment.",
    detail: "Intent classification: High / Medium / Low",
    span: "lg:col-span-1",
  },
  {
    icon: Instagram,
    name: "Instagram Engine",
    phase: "Phase C",
    copy: "Creators, educators and niche pages — with collaboration windows, DM drafts and Live ideas, all human-approved.",
    detail: "Collaboration window detection",
    span: "lg:col-span-2",
  },
];

export default function Engines() {
  return (
    <section id="engines" data-testid="engines-section" className="border-t border-white/10">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D4AF37]">
              One Core. Five Engines.
            </p>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
              Every stage your buyers already gather around.
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-zinc-500">
            A single AI distribution core powers five acquisition modules — sharing one database,
            one scoring system, one CRM, and one intelligence layer.
          </p>
        </motion.div>

        <div className="mt-16 grid gap-6 lg:grid-cols-4">
          {ENGINES.map((e, i) => (
            <motion.article
              key={e.name}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.07 }}
              data-testid={`engine-card-${e.name.toLowerCase().split(" ")[0]}`}
              className={`group relative overflow-hidden border border-[#27272A] bg-[#0A0A0A] p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/50 ${e.span}`}
            >
              {e.image && (
                <>
                  <img
                    src={e.image}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover opacity-25 transition-opacity duration-500 group-hover:opacity-40"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/40" />
                </>
              )}
              <div className="relative flex h-full flex-col">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 backdrop-blur-md">
                    <e.icon className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    {e.phase}
                  </span>
                </div>
                <h3 className="mt-8 font-serif text-2xl font-semibold">{e.name}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-zinc-400">{e.copy}</p>
                <p className="mt-6 flex items-center gap-2 border-t border-white/10 pt-4 text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-500">
                  <ArrowUpRight className="h-3.5 w-3.5 text-[#D4AF37]" />
                  {e.detail}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
